import type { AnalysisResult } from "../scrapers/types";
import { ScoreGauge } from "./ScoreGauge";
import { SignalBreakdown } from "./SignalBreakdown";

const VERDICT_CONFIG = {
  LIKELY_REAL: {
    label: "Likely Real",
    color: "#22c55e",
    bg: "rgba(34,197,94,0.06)",
    border: "rgba(34,197,94,0.15)",
    emoji: "✅",
  },
  POSSIBLY_GHOST: {
    label: "Possibly Ghost",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.06)",
    border: "rgba(245,158,11,0.15)",
    emoji: "⚠️",
  },
  LIKELY_GHOST: {
    label: "Likely Ghost",
    color: "#ff4444",
    bg: "rgba(255,68,68,0.06)",
    border: "rgba(255,68,68,0.15)",
    emoji: "👻",
  },
  NOT_A_JOB: {
    label: "Not a Job Posting",
    color: "#6b7280",
    bg: "rgba(107,114,128,0.06)",
    border: "rgba(107,114,128,0.15)",
    emoji: "🚫",
  },
};

const PLATFORM_LABELS: Record<string, string> = {
  linkedin: "LinkedIn",
  indeed: "Indeed",
  naukri: "Naukri",
  wellfound: "Wellfound",
  unknown: "Unknown",
};

interface AnalysisResultsProps {
  result: AnalysisResult;
  onReset: () => void;
}

export function AnalysisResults({ result, onReset }: AnalysisResultsProps) {
  const cfg = VERDICT_CONFIG[result.verdict];

  return (
    <div className="min-h-screen min-h-dvh px-3 sm:px-4 py-6 sm:py-10">
      <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5">
        {/* Back link */}
        <div className="animate-fade-in">
          <button
            onClick={onReset}
            className="mono-label transition-colors active:opacity-70"
            style={{ color: "var(--text-tertiary)" }}
            onMouseEnter={(e) => ((e.target as HTMLElement).style.color = "var(--text-secondary)")}
            onMouseLeave={(e) => ((e.target as HTMLElement).style.color = "var(--text-tertiary)")}
          >
            ← New investigation
          </button>
        </div>

        {/* Job metadata card */}
        <div
          className="glass-card p-4 sm:p-5 animate-fade-in"
          style={{ animationDelay: "50ms", animationFillMode: "both" }}
        >
          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4 overflow-hidden">
            <span
              className="mono-label px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--border)",
              }}
            >
              {PLATFORM_LABELS[result.platform] ?? result.platform}
            </span>
            <span
              className="mono-label px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full capitalize whitespace-nowrap"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--border)",
              }}
            >
              {result.confidence} confidence
            </span>
            {result.jobData.scrapeFailed && (
              <span
                className="mono-label px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full whitespace-nowrap"
                style={{
                  background: "rgba(245,158,11,0.06)",
                  color: "var(--ghost-amber)",
                  border: "1px solid rgba(245,158,11,0.15)",
                }}
              >
                Scrape limited
              </span>
            )}
            {result.jobData.scrapeSources && result.jobData.scrapeSources.length > 0 && (
              <span
                className="mono-label px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full truncate max-w-[200px] sm:max-w-none"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid var(--border)",
                }}
                title={`Data sources: ${result.jobData.scrapeSources.join(", ")}`}
              >
                via {result.jobData.scrapeSources.join(", ")}
              </span>
            )}
          </div>

          {/* Job info */}
          {result.jobData.title ? (
            <h2
              className="text-lg sm:text-xl text-white mb-1 leading-snug"
              style={{ fontFamily: "var(--font-display)", fontWeight: 400 }}
            >
              {result.jobData.title}
            </h2>
          ) : (
            <h2
              className="text-lg sm:text-xl mb-1"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--text-tertiary)",
              }}
            >
              Title unavailable
            </h2>
          )}

          {result.jobData.company && (
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {result.jobData.company}
            </p>
          )}
          {result.jobData.location && (
            <p className="text-xs mt-0.5" style={{ color: "var(--text-tertiary)" }}>
              {result.jobData.location}
            </p>
          )}
          {result.jobData.postedDaysAgo !== undefined && (
            <p
              className="mt-1.5 sm:mt-2"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.6rem",
                color: "var(--text-tertiary)",
              }}
            >
              Posted {result.jobData.postedDaysAgo}d ago
              {result.jobData.applicantCount !== undefined &&
                ` · ${result.jobData.applicantCount} applicants`}
            </p>
          )}
        </div>

        {/* Scrape-limited warning */}
        {(result.jobData.scrapeFailed || result.jobData.scrapePartial) && (
          <div
            className="glass-card p-3.5 sm:p-4 animate-fade-in flex gap-3 items-start"
            style={{
              background: "rgba(245,158,11,0.04)",
              border: "1px solid rgba(245,158,11,0.15)",
              animationDelay: "100ms",
              animationFillMode: "both",
            }}
          >
            <span className="text-base leading-none mt-0.5 shrink-0" style={{ color: "#f59e0b" }}>⚠</span>
            <div>
              <p className="text-xs sm:text-sm" style={{ color: "#f59e0b" }}>
                Scraping was limited for this listing
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--text-tertiary)" }}>
                Some data couldn't be extracted, so the analysis may be less accurate.
                For better results, try again and paste the full job description.
              </p>
            </div>
          </div>
        )}

        {result.verdict === "NOT_A_JOB" ? (
          <>
            {/* Not a job posting notice */}
            <div
              className="glass-card flex flex-col items-center gap-4 sm:gap-5 py-8 sm:py-10 animate-fade-in-scale"
              style={{
                borderColor: cfg.border,
                animationDelay: "150ms",
                animationFillMode: "both",
              }}
            >
              <span className="text-5xl sm:text-6xl">🚫</span>

              <div
                className="flex items-center gap-2 sm:gap-2.5 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-base sm:text-lg font-medium"
                style={{
                  fontFamily: "var(--font-display)",
                  color: cfg.color,
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                }}
              >
                {cfg.label}
              </div>

              <p className="mono-label text-center px-4" style={{ color: "var(--text-tertiary)" }}>
                This link doesn't appear to be a job posting
              </p>
            </div>

            {/* AI explanation */}
            <div
              className="glass-card p-4 sm:p-5 animate-fade-in"
              style={{ animationDelay: "250ms", animationFillMode: "both" }}
            >
              <div className="flex items-center gap-2 sm:gap-2.5 mb-2.5 sm:mb-3">
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(129,140,248,0.1)",
                    border: "1px solid rgba(129,140,248,0.15)",
                  }}
                >
                  <span className="text-xs">🤖</span>
                </div>
                <span className="mono-label" style={{ color: "var(--investigate)" }}>
                  Why this isn't a job posting
                </span>
              </div>
              <p
                className="text-xs sm:text-sm leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {result.reasoning}
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Score + verdict */}
            <div
              className="glass-card flex flex-col items-center gap-4 sm:gap-5 py-8 sm:py-10 animate-fade-in-scale"
              style={{
                borderColor: cfg.border,
                animationDelay: "150ms",
                animationFillMode: "both",
              }}
            >
              <ScoreGauge score={result.totalScore} verdict={result.verdict} />

              <div
                className="flex items-center gap-2 sm:gap-2.5 px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-base sm:text-lg font-medium"
                style={{
                  fontFamily: "var(--font-display)",
                  color: cfg.color,
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                }}
              >
                <span>{cfg.emoji}</span>
                {cfg.label}
              </div>

              <p className="mono-label">
                Ghost Probability · {result.totalScore}/100
              </p>
            </div>

            {/* AI Reasoning */}
            <div
              className="glass-card p-4 sm:p-5 animate-fade-in"
              style={{ animationDelay: "250ms", animationFillMode: "both" }}
            >
              <div className="flex items-center gap-2 sm:gap-2.5 mb-2.5 sm:mb-3">
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center shrink-0"
                  style={{
                    background: "rgba(129,140,248,0.1)",
                    border: "1px solid rgba(129,140,248,0.15)",
                  }}
                >
                  <span className="text-xs">🤖</span>
                </div>
                <span className="mono-label" style={{ color: "var(--investigate)" }}>
                  AI Analysis
                </span>
              </div>
              <p
                className="text-xs sm:text-sm leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {result.reasoning}
              </p>
            </div>

            {/* Signal breakdown */}
            <div
              className="animate-fade-in"
              style={{ animationDelay: "350ms", animationFillMode: "both" }}
            >
              <SignalBreakdown signals={result.signals} />
            </div>
          </>
        )}

        {/* CTA */}
        <button
          onClick={onReset}
          className="w-full py-3.5 sm:py-4 rounded-xl sm:rounded-2xl font-semibold text-sm tracking-wide transition-all duration-300 animate-fade-in active:scale-[0.98]"
          style={{
            background: "#ffffff",
            color: "#08080a",
            animationDelay: "450ms",
            animationFillMode: "both",
            boxShadow: "0 2px 16px rgba(255,255,255,0.08)",
          }}
          onMouseEnter={(e) => ((e.target as HTMLElement).style.opacity = "0.88")}
          onMouseLeave={(e) => ((e.target as HTMLElement).style.opacity = "1")}
        >
          Investigate Another Job →
        </button>

        <p className="text-center pb-4 sm:pb-6 mono-label">
          Investigated {new Date(result.analyzedAt).toLocaleString()}
        </p>
      </div>
    </div>
  );
}
